import { useTranslation } from "react-i18next";
import Skeleton from "../Skeleton/Skeleton";

// ---------------------------------------------------------------------------
// PanelKit — one layout, surface and control vocabulary for the seven tab
// panels of the timetable workspace.
//
// Every panel used to invent its own. Measured across the seven:
//
//   * FOUR content widths — 1600px (Overview), 1152px (Lessons/Rooms/Rules),
//     896px (Generate, Import) and 768px (School week) — and the last three
//     were `max-w-*` with no `mx-auto`, so they hugged the LEFT edge of a
//     CENTRED column. The right edge of the content therefore stopped hundreds
//     of pixels short of the tab strip's own underline.
//   * Worse, the width lived on the same box as the BackLink and the tab
//     strip, so switching tabs RESIZED AND RE-CENTRED THE NAVIGATION — the
//     chrome moved out from under the cursor that had just clicked it.
//   * Two card radii: `rounded-2xl` (Lessons, Rooms, School week) and
//     `rounded-[28px]` (Generate, Rules, Import) — the only eleven uses of
//     that value in the whole app, against 50 `rounded-2xl`.
//   * Two primary-button languages, with several panels rendering BOTH: an
//     `text-sm font-semibold` pill in the header and an 11px uppercase-black
//     one in the empty state, four inches apart, for the same action.
//   * Three empty states differing only in `py-12` / `py-16` / `py-20`, two
//     error cards differing only in `p-6` / `p-10`, and three root rhythms
//     (none at all, `space-y-5`, `space-y-6`).
//
// WIDTH IS NOT DECIDED HERE. It belongs to the shell (TimetableWorkspace),
// which now applies one value to every tab. A panel picks nothing but which of
// these surfaces it needs.
// ---------------------------------------------------------------------------

// The app-standard white surface — 35 files already use exactly this.
export const CARD = "bg-white rounded-2xl border border-slate-200 shadow-sm";

// One rhythm for every panel: header, toolbar and body are all 20px apart.
// PanelHeader deliberately carries no bottom margin of its own any more, so
// this is the only number in play.
export const PANEL_STACK = "space-y-5";

// `text-sm font-semibold` wins the primary-button split: it is the app-wide
// majority (19 uses against 12) and it stays legible, which 11px black
// uppercase at `tracking-widest` does not. Every button in the workspace is
// one of these four, at one size.
const BTN_BASE =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";

export const BTN = {
  primary: `${BTN_BASE} bg-indigo-600 text-white shadow-sm hover:bg-indigo-700`,
  secondary: `${BTN_BASE} border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50`,
  danger: `${BTN_BASE} bg-rose-600 text-white shadow-sm hover:bg-rose-700`,
  // No border, no fill — for the third action in a row, where a second
  // outlined button would read as equal in weight to the first.
  quiet: `${BTN_BASE} text-slate-600 hover:bg-slate-100`,
  // Tinted secondaries. They are whole variants rather than colour utilities
  // appended to `secondary`, because appending `bg-indigo-50` to a string that
  // already contains `bg-white` resolves by the order Tailwind happens to emit
  // the palette in, not by the order they appear in the attribute — it works
  // by luck and breaks silently when a palette or a plugin reorders.
  accent: `${BTN_BASE} border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100`,
  dangerQuiet: `${BTN_BASE} border border-rose-200 bg-white text-rose-700 hover:bg-rose-50`,
};

// In-row Edit / Delete. Lessons and Rooms already agreed on this pair; Rules
// used a 10px uppercase-black text link in slate-400 instead, so the same two
// actions on the same kind of table read as a different control one tab over
// (and at 2.9:1 against white, below the contrast floor).
const ROW_ACTION =
  "rounded-lg px-2 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1";

export const ROW_BTN = {
  edit: `${ROW_ACTION} text-indigo-600 hover:bg-indigo-50`,
  delete: `${ROW_ACTION} text-rose-600 hover:bg-rose-50`,
};

// One header row for the three data tables. They had drifted to
// `bg-slate-50` + `text-slate-500 font-bold` (Lessons, Rooms) versus
// `bg-slate-50/50` + `text-slate-400 font-black` (Rules).
export const TABLE_HEAD =
  "bg-slate-50 text-left text-xs font-semibold text-slate-500";

// The panel root. Content only — the shell owns width, padding and background.
export function Panel({ children, className = "" }) {
  return <div className={`${PANEL_STACK} ${className}`}>{children}</div>;
}

// A white card. `padded` off for cards whose child is a table and pads its own
// cells, which is why the two never agreed on where the padding lived.
// `rest` is forwarded so a caller can still put `aria-busy` / `role` on the
// surface itself rather than having to wrap it in another div to do so.
export function Card({ children, className = "", padded = true, ...rest }) {
  return (
    <div
      {...rest}
      className={`${CARD} ${padded ? "p-6" : "overflow-hidden"} ${className}`}
    >
      {children}
    </div>
  );
}

// The filter/search strip that sits between the header and the body. Lessons
// and Rooms each hand-rolled it; Rules and Overview used a different gap.
export function PanelToolbar({ children, className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {children}
    </div>
  );
}

// Byte-identical in ActivitiesPage and RoomsPage before this existed.
export function SearchInput({ value, onChange, ariaLabel, placeholder }) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M21 21l-4.3-4.3m1.8-4.7a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"
        />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || t("common.search")}
        aria-label={ariaLabel}
        className="w-44 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm transition outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 sm:w-56"
      />
    </div>
  );
}

// The REFETCH indicator, for the second load onward.
//
// Every panel here already had a first-load skeleton and nothing at all for the
// loads that follow. Changing a filter on Lessons refires the query and leaves
// the previous page's rows on screen, unchanged, for as long as the round trip
// takes — so the honest reading of the screen is "your filter did nothing".
// Blanking back to the skeleton is the wrong fix (the whole table jumps, and on
// a fast connection it strobes); the pattern is stale content held in place at
// reduced contrast, with this pill in the toolbar saying why.
//
// `role="status"` rather than `aria-live="assertive"`: it is a background
// refresh, and it must not interrupt someone mid-sentence in the search box.
export function Refreshing({ label }) {
  const { t } = useTranslation();
  return (
    <span
      role="status"
      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500"
    >
      <span
        aria-hidden="true"
        className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"
      />
      {label || t("common.updating")}
    </span>
  );
}

// The class pair that holds stale content in place while `Refreshing` explains
// it. Pointer events go off with the fade: a row you click during a refetch is
// a row from the list you are replacing.
export const STALE = "pointer-events-none opacity-50 transition-opacity";

// The first-load placeholder for the three data tables. Lessons and Rooms had
// byte-similar copies of this and Rules had none at all — it fell back to three
// generic bars in a card, which is a different shape from the table that
// replaces it, so the content jumped on arrival.
//
// `columns` is a list of Tailwind width classes; add responsive `hidden md:block`
// to a column that the real table also hides, or the placeholder claims a
// column count the table does not have.
export function TableSkeleton({ rows = 6, columns }) {
  return (
    <div aria-hidden="true" className={`${CARD} overflow-hidden`}>
      <div className="h-10 bg-slate-50" />
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-6 border-t border-slate-100 px-6 py-3.5"
        >
          {columns.map((width, j) => (
            <Skeleton key={j} className={`h-3.5 ${width}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

// One empty state. `children` carries the teaching content some panels add
// (Rooms lists example rooms) without forking the container again.
export function EmptyState({ iconPath, title, hint, action, children }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white/60 px-6 py-16 text-center">
      {iconPath && (
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
          <svg
            className="h-7 w-7 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
              d={iconPath}
            />
          </svg>
        </span>
      )}
      <p className="text-sm font-bold text-slate-700">{title}</p>
      {hint && (
        <p className="mt-1.5 max-w-md text-xs leading-relaxed text-slate-500">
          {hint}
        </p>
      )}
      {children && <div className="mt-6 w-full max-w-xl text-left">{children}</div>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

// One failure card, with the retry the panels had all spelled slightly
// differently.
export function ErrorState({ title, onRetry, retryLabel }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="rounded-2xl border border-rose-100 bg-rose-50 px-6 py-10 text-center"
    >
      <p className="text-sm font-bold text-rose-700">{title}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={`mt-4 ${BTN.dangerQuiet}`}
        >
          {retryLabel || t("common.retry")}
        </button>
      )}
    </div>
  );
}

// Body tones are SOLID, not `/90`. The panels' hand-rolled notices used an
// alpha (`text-amber-700/80`) and copying that here would have spread a
// measurable contrast loss to every notice in the workspace at once: amber-700
// on amber-50 is 4.8:1, and at 90% over that background it falls under the
// 4.5:1 floor for the 12px text these carry.
const NOTICE_TONE = {
  amber: {
    box: "border-amber-200 bg-amber-50",
    title: "text-amber-900",
    body: "text-amber-800",
  },
  rose: {
    box: "border-rose-200 bg-rose-50",
    title: "text-rose-900",
    body: "text-rose-800",
  },
  emerald: {
    box: "border-emerald-200 bg-emerald-50",
    title: "text-emerald-900",
    body: "text-emerald-800",
  },
};

// The tinted advisory block. Generate alone hand-rolled three of these with
// `p-5`, `p-5` and `p-4`; Import, Rules and School week had their own.
export function Notice({ tone = "amber", title, children, action, role }) {
  const palette = NOTICE_TONE[tone] || NOTICE_TONE.amber;
  return (
    <div
      role={role}
      className={`flex flex-wrap items-start justify-between gap-4 rounded-2xl border p-5 ${palette.box}`}
    >
      <div className="min-w-0">
        {title && (
          <p className={`text-sm font-bold ${palette.title}`}>{title}</p>
        )}
        {children && (
          <div className={`mt-1 text-xs font-medium leading-relaxed ${palette.body}`}>
            {children}
          </div>
        )}
      </div>
      {action}
    </div>
  );
}
